
import moment from 'moment'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { money, resolveType, toAlpha } from '~/util/string'
import { setReactive } from '~/util/reactive'
import { periodLabel, reportPeriod } from '~/util/employer-report'

const NA = 'N/A'

// The "Drug Search Queries" per-drug price columns (flow/crm/drug_savings.py):
// the prices of one typical 30-day fill of the drug, not sums, and the
// annualized claims worked out here from its full price, so a price staff
// type into a blank cell (found on GoodRx's site, say) counts in the row and
// in the period's total. A typed price is the drug's, kept in
// `drug_fill_prices` and saved with the report, so it counts in every
// period's row for the drug. The claims multiply the drug's people, or its
// queries, as the report's `drug_claims_by` says; the People column shows
// the people. The report's `drug_savings_hidden` flag takes the columns off.
// The month table is the query counts alone (2026-10-31).
const DRUG_PEOPLE_FIELD = { key: 'people', label: 'People' }
const DRUG_PRICE_FIELDS = [
    { key: 'fill_copay', label: 'Group Plan Copay', money: true },
    { key: 'fill_lowest', label: 'Lowest Option', money: true },
    { key: 'lowest_source', label: 'Source' },
    { key: 'fill_price', label: 'Est. Full Price (1-Month Fill)', price: true },
    { key: 'claims', label: 'Est. Annualized Claims', claims: true },
]
const FILLS_A_YEAR = 12

// the drug table's money, to the dollar (2026-10-31): cents on an estimate
// read as a precision it hasn't got. Sums add the unrounded amounts.
const wholeDollars = value => money(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

// a price typed as "$1,250.50" or "1250.5"; null, the counted price again,
// for anything without a number in it
const typedDollars = (text) => {
    const number = parseFloat(`${_.isNil(text) ? '' : text}`.replace(/[^0-9.]/g, ''))
    return isNaN(number) ? null : _.round(number, 2)
}

export default {
    props: {
        data: {
            type: Object,
            default: null
        },
        raw: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            parsedData: null,
        }
    },
    computed: {
        ...mapGetters([
            'user',
            'crmBase',
            'isServer',
            // The renderer has no window.STATE so this is false there — fine, since
            // the sections it gates are also .d-print-none and v-if='canAgent', and
            // renderer.js runs wkhtmltopdf with --print-media-type.
            'demo'
        ]),
        report () {
            return this.parsedData
        },
        yearsFields () {
            return (this.report?.years || []).map(y => `${y}`)
        },
        periods () {
            return _.sortBy(this.data.periods.map((p) => {
                // UTC, not local: these bounds are calendar boundaries, not
                // instants. The backend stamps them tz-aware at UTC midnight /
                // 23:59:59.999999 (flow/crm/groups.py:57-58, since 395d2c4f), so
                // reading them in the browser's zone moves the quarter's first
                // or last millisecond onto a neighbouring month -- a spurious
                // "Oct 2026" row on a Q3 report from UTC+3, a spurious "Jun
                // 2026" row and a "Q2 2026" total label from the US, and a YTD
                // section headed with the previous year. See
                // test/employer-report-periods.spec.js.
                const sd = moment.utc(p.s_date).startOf('month')
                const ed = moment.utc(p.e_date).endOf('month')
                if (/ytd/i.test(p.postfix)) {
                    p.label = `YTD ${sd.year()}`
                } else {
                    // The primary period and its last-year twin carry no postfix
                    // that says what they are; a year to date is told by its bounds.
                    p.label = reportPeriod(sd, ed).label
                }
                p.date = ed.clone()
                p.datestr = p.date.format('MM/DD/YYYY')
                const months = []
                // Compare with month granularity rather than `ed > sd ||
                // sd.format('M') === ed.format('M')`: that tail read month
                // *numbers* with no year, so on a range ending before it starts
                // 'Jul 2027' matched 'Jul 2026' and the walk emitted one month
                // from a year the report never covered.
                while (sd.isSameOrBefore(ed, 'month')) {
                    months.push({
                        // clone: `sd` is advanced below, and an un-cloned
                        // reference left every month pointing at the same
                        // instant -- the month past the end of the range.
                        date: sd.clone(),
                        datestr: sd.format('MM/DD/YYYY'),
                        key: sd.format('MMM YYYY'),
                        label: sd.format('MMM YYYY'),
                        year: sd.year(),
                        month: sd.month()
                    })
                    sd.add(1, 'month')
                }
                p.months = months
                return p
            }), i => i.date)
        },
        months () {
            return _.sortBy(this.periods.reduce((a, p) => {
                return a.concat(p.months)
            }, []), i => i.date)
        },
        quarters () {
            return this.periods.reduce((a, p) => {
                return a.concat({
                    key: p.label,
                })
            }, [])
        },
        // The Drug Search Queries tables list the quarters in order and the YTD
        // comparison after all of them. Sorted by end date like every other
        // table, the YTD (which ends today) lands before the current quarter.
        drugPeriods () {
            return _.sortBy(this.periods, p => (/ytd/i.test(p.postfix) ? 1 : 0))
        },
        drugPriceFields () {
            return this.report && this.report.drug_savings_hidden ? [] : DRUG_PRICE_FIELDS
        },
        // Per person unless the report says per query: Wegovy's 127 queries in
        // 2026 were 21 people's, and a year of claims is a person's, not a page load's.
        drugClaimsBy () {
            return this.report && this.report.drug_claims_by === 'queries' ? 'queries' : 'people'
        },
        drugQueryFields () {
            if (!this.drugPriceFields.length) {
                return []
            }
            return this.drugClaimsBy === 'people' ? [DRUG_PEOPLE_FIELD, ...this.drugPriceFields] : this.drugPriceFields
        },
        // The month table top to bottom: each month once, a total after each
        // quarter's months, and a year to date's total after its year's last
        // quarter. The periods overlap (Q3 and the YTD both hold July), and
        // listing each period's months in turn showed them twice.
        drugMonthRows () {
            const queries = m => _.get(this.report, ['drug_cost_queries_map_by_month', m.key, 'queries']) || 0
            const quarter = m => `Q${m.date.quarter()} ${m.year}`
            const months = _.sortBy(_.uniqBy(this.months, 'key'), m => m.date.valueOf())
            const ytds = this.periods.filter(p => /^YTD/.test(p.label))
            const rows = []
            months.forEach((m, i) => {
                rows.push({ label: m.label, queries: queries(m) })
                const next = months[i + 1]
                if (next && quarter(next) === quarter(m)) {
                    return
                }
                rows.push({ label: quarter(m), queries: _.sum(months.filter(o => quarter(o) === quarter(m)).map(queries)), total: true })
                if (!next || next.year !== m.year) {
                    ytds.filter(p => p.date.year() === m.year).forEach((p) => {
                        rows.push({ label: p.label, queries: _.sum(p.months.map(queries)), total: true })
                    })
                }
            })
            return rows
        },
        // The form's "Show only Drug Search Queries": the group's Medicare
        // sections are left out of the page and the PDF altogether.
        drugsOnly () {
            return !!(this.report && this.report.drugs_only)
        },
        // A drugs-only report is the Drug Search Queries section, so it shows
        // whatever the hide flag says: the backend hides the section by
        // default, and unhiding it by hand made the report a hand-edited copy,
        // frozen, its prices no longer updated without Fresh (2026-11-01).
        drugSectionHidden () {
            return !!(this.report && this.report.drug_cost_queries_hidden) && !this.drugsOnly
        },
    },
    watch: {
        data: {
            deep: true,
            handler () {
                this.parsedData = this.parseReport(this.data)
            }
        },
        'parsedData.utilization_map_by_month': {
            deep: true,
            handler () {
                this.saveChartsAsPng()
            }
        }
    },
    mounted () {
        this.setGlobal('$employerReport', this)
    },
    created () {
        // per instance, so blur can flush it (a bound method has no .flush)
        this.debouncedCellEdit = _.debounce(edit => this.onKeyEdit(edit), 300)
        this.parsedData = this.parseReport(this.data)
    },
    methods: {
        toAlpha,
        periodLabel,
        // A drug's full price: the one typed over it, else its typical fill's.
        fillPrice (row) {
            const typed = _.get(this.report, ['drug_fill_prices', row.query])
            if (_.isNumber(typed)) {
                return typed
            }
            return _.isNumber(row.fill_price) ? row.fill_price : null
        },
        // Full price x 12 x the drug's people, or its drug cost queries (every
        // time a card loaded its price: adding the drug, each plan switch, each
        // reload), as asked for at first (2026-10-31). None for a row from
        // before the people were counted.
        annualClaims (row) {
            const price = this.fillPrice(row)
            const count = this.drugClaimsBy === 'queries' ? row.total : row.people
            return price === null || _.isNil(count) ? null : _.round(price * FILLS_A_YEAR * count, 2)
        },
        // blank, not $0, where there is no price yet and on a report saved
        // before the columns existed
        drugCell (row, field) {
            const value = field.claims ? this.annualClaims(row) : field.price ? this.fillPrice(row) : row[field.key]
            if (_.isNil(value)) {
                return ''
            }
            return field.money || field.price || field.claims ? wholeDollars(value) : `${value}`
        },
        sumClaims (rows) {
            const claims = (rows || []).map(r => this.annualClaims(r)).filter(c => c !== null)
            return claims.length ? wholeDollars(_.sum(claims)) : ''
        },
        // A year to date's drug list is one list, its quarters' totals right
        // above its own: each drug's queries and people in the quarter, and
        // its claims at the drug's price, the same as the list's. Per query
        // the quarters add up to the year; per person they need not -- someone
        // who looked a drug up in two quarters is one of each quarter's people
        // and one of the year's. None on a report from before the quarters.
        drugQueryQuarters (p) {
            const quarters = this.report && this.report[`drug_cost_queries_by_query_quarter${p.postfix}`]
            if (!/^YTD/.test(p.label) || !quarters) {
                return []
            }
            const drugs = _.keyBy(this.report[`drug_cost_queries_by_query${p.postfix}`] || [], 'query')
            const quarter = m => `Q${m.date.quarter()} ${m.year}`
            return _.uniq(p.months.map(quarter)).map((label) => {
                const rows = quarters.filter(r => r.quarter === label)
                const claims = rows.map(r => this.annualClaims({ ...(drugs[r.query] || { query: r.query }), total: r.total, people: r.people })).filter(c => c !== null)
                return {
                    label,
                    queries: _.sum(rows.map(r => r.total || 0)),
                    claims: claims.length ? wholeDollars(_.sum(claims)) : '',
                }
            })
        },
        // On screen only, like a typed count: print and the PDF look the same.
        fillPriceTyped (row) {
            return !this.isServer && _.isNumber(_.get(this.report, ['drug_fill_prices', row.query]))
        },
        // which dose the price is for and where it came from, and under a
        // typed one what was counted
        fillPriceTitle (row) {
            const counted = _.isNumber(row.fill_price) ? `${money(row.fill_price)}${row.fill_price_source ? `, ${row.fill_price_source}` : ''}` : 'no price'
            if (this.fillPriceTyped(row)) {
                return `Typed by hand (counted: ${counted})`
            }
            if (!row.fill_dose) {
                return null
            }
            return `For ${row.fill_dose}${row.fill_price_source ? ` (${row.fill_price_source})` : ''}`
        },
        rowClass (item) {
            return item.variant ? `table-${item.variant}` : undefined
        },
        getUtilizationChartData () {
            const len = this.report?.utilization_table_by_month?.length
            const map = _.cloneDeep(this.report?.utilization_map_by_month)
            if (!len) {
                return null
            }
            const bars = [].concat(this.months).concat(this.quarters)
            this.periods.forEach((p) => {
                map[p.label] = {
                    consultations: _.sum(p.months.map(m => map[m.key]?.consultations || 0)),
                    communications: _.sum(p.months.map(m => map[m.key]?.communications || 0)),
                    webinars: _.sum(p.months.map(m => map[m.key]?.webinars || 0)),
                }
            })

            const datasets = [
                {
                    label: 'Consultations (1)',
                    backgroundColor: '#008CCD',
                    data: bars.map(m => map[m.key]?.consultations || 0),
                },
                {
                    label: 'Calls/Emails (2)',
                    backgroundColor: '#ff661f',
                    data: bars.map(m => map[m.key]?.communications || 0),
                },
                {
                    label: 'Webinars (3)',
                    backgroundColor: '#28a745',
                    data: bars.map(m => map[m.key]?.webinars || 0),
                },
            ]
            return {
                labels: bars.map(b => b.key),
                datasets,
            }
        },
        getUtilizationChartOptions () {
            return {
                title: {
                    display: false,
                    text: 'Utilization Chart',
                },
                legend: {
                    display: true,
                },
                scales: {
                    x: {
                        stacked: true,
                    },
                    y: {
                        stacked: true,
                    },
                },
                animation: false
            }
        },
        getPlansTypesChartData (period) {
            const dataMap = {
                mapds: { total: 0, label: 'MAPD' },
                supps: { total: 0, label: 'SUPP' },
                pdps: { total: 0, label: 'PDP' },
                unknowns: { total: 0, label: 'N/A' },
            }
            period.months.forEach((m) => {
                dataMap.mapds.total += _.get(this.report, `plans_by_type_by_month_map.${m.key}.mapds`) || 0
                dataMap.supps.total += _.get(this.report, `plans_by_type_by_month_map.${m.key}.supps`) || 0
                dataMap.pdps.total += _.get(this.report, `plans_by_type_by_month_map.${m.key}.pdps`) || 0
                dataMap.unknowns.total += _.get(this.report, `plans_by_type_by_month_map.${m.key}.unknowns`) || 0
            })
            const values = _.values(dataMap)
            const total = _.sumBy(values, 'total')
            if (!total) {
                return null
            }
            return {
                datasets: [{
                    data: values.map(v => v.total),
                    backgroundColor: ['#28a745', '#ff661f', '#008CCD', '#dc3545'],
                }],
                labels: values.map(v => `${v.label} (${v.total})`),
            }
        },
        getPlansTypesChartOptions (title) {
            return {
                title: {
                    display: true,
                    text: title || 'Closed Plans by Type',
                },
                responsive: true,
                tooltips: {
                    display: true,
                },
                legend: {
                    display: true,
                },
            }
        },
        parseReport (report) {
            if (report.parsed) {
                return report
            }
            report.parsed = true
            const formatter = value => value || 0

            report.utilization_by_month_fields = [
                { key: 'key', label: 'Month' },
                { key: 'consultations', label: 'Consultations', sub: '(1)', formatter },
                { key: 'communications', label: 'Calls/Emails', sub: '(2)', formatter },
                { key: 'webinars', label: 'Webinar Attendees', sub: '(3)', formatter },
                { key: 'total', label: 'Total', formatter },
            ]
            report.utilization_map_by_month = {}
            const utilizationByMonthMap = (field, period) => (record) => {
                record.date = record.year && record.month ? moment(`${record.month}/01/${record.year}`, 'MM/DD/YYYY').startOf('month') : moment(record.month, 'YYYY-MM-DD').startOf('month')
                // Skip records we can't place in a real month instead of dumping them
                // into an "Invalid date NaN" bucket. That bucket is never iterated by
                // the month rows or the period totals, so its counts silently vanish
                // from the table (e.g. webinar attendees). See drschoice/flow#595.
                if (!record.date || !record.date.isValid()) {
                    return
                }
                record.datestr = record.date.format('MM/DD/YYYY')
                record.year = record.year || record.date.year()
                record.month = record.month || record.date.month()
                record.key = `${record.date.format('MMM')} ${record.year}`
                record[field] = record.total || 0
                report.utilization_map_by_month[record.key] = { ...(report.utilization_map_by_month[record.key] || {}), [field]: record[field] }
            }
            this.periods.forEach((p) => {
                p.months.forEach((m) => {
                    report.utilization_map_by_month[m.key] = { key: m.key, date: m.date, datestr: m.datestr, month: m.month, year: m.year, total: 0 }
                })
                ;(report[`consultations_scheduled_by_month${p.postfix}`] || []).map(utilizationByMonthMap('consultations', p));
                (report[`communications_by_month${p.postfix}`]?.records || []).map(utilizationByMonthMap('communications', p));
                (report[`roadmaps_completed_by_month${p.postfix}`] || []).map(utilizationByMonthMap('roadmaps', p));
                (report[`webinars_attendees_by_month${p.postfix}`]?.records || []).map(utilizationByMonthMap('webinars', p))
            })
            report.utilization_table_by_month = _.values(report.utilization_map_by_month)

            report.utilization_by_state_fields = [
                { key: 'key', label: 'State' },
                { key: 'consultations', label: 'Consultations', formatter },
                { key: 'communications', label: 'Calls/Emails', formatter },
                { key: 'total', label: 'Total', formatter },
            ]
            this.periods.forEach((p) => {
                report[`utilization_map_by_state${p.postfix}`] = {}
                const utilizationByStateMap = field => (record) => {
                    record.key = record.state || record.PersonMailingState || NA
                    record[field] = record.total || 0
                    report[`utilization_map_by_state${p.postfix}`][record.key] = {
                        ...(report[`utilization_map_by_state${p.postfix}`][record.key] || { total: 0 }),
                        ..._.omit(record, ['total'])
                    }
                    report[`utilization_map_by_state${p.postfix}`][record.key].total = (report[`utilization_map_by_state${p.postfix}`][record.key].total || 0) + (record[field] || 0)
                }
                ;(report[`consultations_scheduled_by_state${p.postfix}`] || []).map(utilizationByStateMap('consultations', p));
                (report[`communications_by_state${p.postfix}`]?.records || []).map(utilizationByStateMap('communications', p));
                (report[`roadmaps_completed_by_state${p.postfix}`] || []).map(utilizationByStateMap('roadmaps', p))
                // add N/A row
                if (!report[`utilization_map_by_state${p.postfix}`][NA]) {
                    report[`utilization_map_by_state${p.postfix}`][NA] = { key: NA, consultations: 0, communications: 0, roadmaps: 0, total: 0 }
                }
                const values = _.values(report[`utilization_map_by_state${p.postfix}`])
                values.sort((m1, m2) => {
                    if (m1.key === NA) return 1
                    if (m2.key === NA) return -1
                    return m1.key < m2.key ? -1 : 1
                })
                report[`utilization_table_by_state${p.postfix}`] = values
            })

            report.hr_utilization_by_month_fields = [
                { key: 'key', label: 'Month' },
                { key: 'communications', label: 'Calls/Emails', formatter },
            ]
            report.hr_utilization_map_by_month = {}
            const hrUtilizationByMonthMap = (field, period) => (record) => {
                record.date = record.year && record.month ? moment(`${record.month}/01/${record.year}`, 'MM/DD/YYYY').startOf('month') : moment(record.month, 'YYYY-MM-DD').startOf('month')
                record.datestr = record.date.format('MM/DD/YYYY')
                record.year = record.year || record.date.year()
                record.month = record.month || record.date.month()
                record.key = `${record.date.format('MMM')} ${record.year}`
                record[field] = record.total || 0
                report.hr_utilization_map_by_month[record.key] = { ...(report.hr_utilization_map_by_month[record.key] || {}), [field]: record[field] }
                report.hr_utilization_map_by_month[record.key].total += (record[field] || 0)
            }
            this.periods.forEach((p) => {
                p.months.forEach((m) => {
                    report.hr_utilization_map_by_month[m.key] = { key: m.key, date: m.date, datestr: m.datestr, month: m.month, year: m.year, total: 0 }
                })
                ;(report[`hr_communications_by_month${p.postfix}`]?.records || []).map(hrUtilizationByMonthMap('communications', p))
            })

            report.enrolled_opportunities_by_month_fields = [
                { key: 'key', label: 'Month' },
                { key: 'transitions', label: 'Transitions', formatter },
            ]
            report.enrolled_opportunities_map_by_month = {}
            const enrolledOppsByMonthMap = (field, period) => (record) => {
                record.date = record.year && record.month ? moment(`${record.month}/01/${record.year}`, 'MM/DD/YYYY').startOf('month') : moment(record.month, 'YYYY-MM-DD').startOf('month')
                record.datestr = record.date.format('MM/DD/YYYY')
                record.year = record.year || record.date.year()
                record.month = record.month || record.date.month()
                record.key = `${record.date.format('MMM')} ${record.year}`
                record[field] = record.total || 0
                report.enrolled_opportunities_map_by_month[record.key] = { ...(report.enrolled_opportunities_map_by_month[record.key] || {}), [field]: record[field] }
                report.enrolled_opportunities_map_by_month[record.key].total += (record[field] || 0)
            }
            this.periods.forEach((p) => {
                p.months.forEach((m) => {
                    report.enrolled_opportunities_map_by_month[m.key] = { key: m.key, date: m.date, datestr: m.datestr, month: m.month, year: m.year, total: 0 }
                })
                ;(report[`enrolled_opportunities_by_month${p.postfix}`]?.records || []).map(enrolledOppsByMonthMap('transitions', p))
                const transitions = _.sumBy(report[`enrolled_opportunities_by_month${p.postfix}`].records, 'transitions')
                report.enrolled_opportunities_map_by_month[p.label] = {
                    date: p.date,
                    key: p.label,
                    transitions,
                    variant: 'warning',
                    total: transitions
                }
                return report
            })

            let plansByTypeByMonthTotalUnknows = 0
            report.plans_by_type_by_month_map = {}
            const plansByTypeByMonthMap = field => (record) => {
                record.date = record.year && record.month ? moment(`${record.month}/01/${record.year}`, 'MM/DD/YYYY').startOf('month') : moment(record.month, 'YYYY-MM-DD').startOf('month')
                record.datestr = record.date.format('MM/DD/YYYY')
                record.year = record.year || record.date.year()
                record.month = record.month || record.date.month()
                record.key = `${record.date.format('MMM')} ${record.year}`
                record[field] = record.total || 0
                report.plans_by_type_by_month_map[record.key] = { ...(report.plans_by_type_by_month_map[record.key] || {}), [field]: record[field] }
                report.plans_by_type_by_month_map[record.key].total += (record[field] || 0)
            }

            report.drug_cost_queries_by_month_fields = [
                { key: 'key', label: 'Month' },
                { key: 'queries', label: 'Drug Cost Queries', formatter },
            ]
            report.drug_cost_queries_map_by_month = {}
            const drugCostQueriesByMonthMap = (field, period) => (record) => {
                record.date = record.year && record.month ? moment(`${record.month}/01/${record.year}`, 'MM/DD/YYYY').startOf('month') : moment(record.month, 'YYYY-MM-DD').startOf('month')
                record.datestr = record.date.format('MM/DD/YYYY')
                record.year = record.year || record.date.year()
                record.month = record.month || record.date.month()
                record.key = `${record.date.format('MMM')} ${record.year}`
                record[field] = record.total || 0
                report.drug_cost_queries_map_by_month[record.key] = { ...(report.drug_cost_queries_map_by_month[record.key] || {}), [field]: record[field] }
                report.drug_cost_queries_map_by_month[record.key].total += (record[field] || 0)
            }
            this.periods.forEach((p) => {
                p.months.forEach((m) => {
                    report.drug_cost_queries_map_by_month[m.key] = { key: m.key, date: m.date, datestr: m.datestr, month: m.month, year: m.year, total: 0 }
                })
                ;(report[`drug_cost_queries_by_month${p.postfix}`] || []).map(drugCostQueriesByMonthMap('queries', p))
            })

            report.drug_cost_queries_by_query_fields = [
                { key: 'query', label: 'Query' },
                { key: 'total', label: 'Drug Cost Queries', formatter },
            ]

            this.periods.forEach((p) => {
                p.months.forEach((m) => {
                    report.plans_by_type_by_month_map[m.key] = { key: m.key, date: m.date, datestr: m.datestr, month: m.month, year: m.year, total: 0 }
                })
                const records = report[`plans_by_type_by_month${p.postfix}`]?.records || []
                const mapdsByMonth = records.filter(r => r.Plan_Type__c === 'MAPD')
                const suppsByMonth = records.filter(r => r.Plan_Type__c === 'SUPP')
                const pdpsByMonth = records.filter(r => r.Plan_Type__c === 'PDP')
                const unknownsByMonth = records.filter(r => r.Plan_Type__c !== 'MAPD' && r.Plan_Type__c !== 'SUPP' && r.Plan_Type__c !== 'PDP')
                mapdsByMonth.map(plansByTypeByMonthMap('mapds', p))
                suppsByMonth.map(plansByTypeByMonthMap('supps', p))
                pdpsByMonth.map(plansByTypeByMonthMap('pdps', p))
                unknownsByMonth.map(plansByTypeByMonthMap('unknowns', p))
                const mapds = _.sumBy(mapdsByMonth, 'mapds')
                const supps = _.sumBy(suppsByMonth, 'supps')
                const pdps = _.sumBy(pdpsByMonth, 'pdps')
                const unknowns = _.sumBy(unknownsByMonth, 'unknowns') || 0
                report.plans_by_type_by_month_map[p.label] = {
                    date: p.date,
                    key: p.label,
                    mapds,
                    supps,
                    pdps,
                    unknowns,
                    variant: 'warning',
                    total: mapds + supps + pdps + unknowns
                }
                plansByTypeByMonthTotalUnknows += unknowns
            })
            report.plans_by_type_by_month_fields = [
                { key: 'key', label: 'Month' },
                { key: 'mapds', label: 'Advantage Plan (Part C)', formatter },
                { key: 'supps', label: 'Supplemental Plan (Medigap)', formatter },
                { key: 'pdps', label: 'Prescription Plan (Part D)', formatter },
                ...(
                    plansByTypeByMonthTotalUnknows
                        ? [{ key: 'unknowns', label: 'Unknown', formatter }]
                        : []
                ),
                { key: 'total', label: 'Total', formatter },
            ]
            return report
        },
        getParsedData () {
            return this.parsedData
        },
        getParsedReport () {
            return this.getParsedData()
        },
        onTextEdit ({ key, value } = {}) {
            this.onKeyEdit({ key, value })
            this.$emit('input', this.parsedData)
        },
        onCellEdit (edit) {
            this.debouncedCellEdit(edit)
        },
        onCellBlur () {
            // the edit typed last may still be waiting on the debounce; save it too
            this.debouncedCellEdit.flush()
            if (this.parsedData.manually_updated) {
                this.$emit('input', this.parsedData)
            }
        },
        // `dollars`: a typed price (typedDollars); a key can be a path array, a
        // drug's name being no path ("PREMPRO 0.3/1.5 28 Day Pack")
        onKeyEdit ({ key, value, typed = false, dollars = false } = {}) {
            if (key) {
                const oldValue = _.get(this.parsedData, key)
                if (dollars) {
                    value = typedDollars(value)
                } else {
                    const oldType = typeof (resolveType(oldValue))
                    value = resolveType(value) || (oldType === 'number' || oldType === 'undefined' ? 0 : null)
                }
                if (typed) {
                    this.markTyped(key, oldValue, value)
                }
                // a month without calls has no `communications` key yet (2026-10-15)
                setReactive(this, this.parsedData, key, value)
                this.$set(this.parsedData, 'manually_updated', +new Date())
                this.$set(this.parsedData, 'manually_updated_by', this.user)
            }
        },
        // A number typed over by hand keeps the counted one it replaced; typing
        // that number back clears the mark. Saved with the report.
        markTyped (key, oldValue, value) {
            if (!this.parsedData.typed_cells) {
                this.$set(this.parsedData, 'typed_cells', {})
            }
            const cells = this.parsedData.typed_cells
            const counted = key in cells ? cells[key].counted : (oldValue || 0)
            if (value === counted) {
                this.$delete(cells, key)
            } else {
                this.$set(cells, key, { counted })
            }
        },
        // The live page tells typed numbers from counted ones; print and the
        // PDF (rendered on the server) show no difference.
        typedCell (key) {
            const cell = !this.isServer && this.parsedData && this.parsedData.typed_cells && this.parsedData.typed_cells[key]
            return cell ? { class: 'employer-report__typed', title: `Typed by hand (counted: ${cell.counted})` } : {}
        },
        saveChartsAsPng () {
            if (!this.$refs.utilizationChartCanvas) {
                return
            }
            const utilizationCanvas = this.$refs.utilizationChartCanvas.$el.querySelector('canvas')
            if (utilizationCanvas) {
                this.onKeyEdit({ key: 'utilizationChartPng', value: utilizationCanvas.toDataURL('image/png') })
            }
        }
    },
}
